import { useEffect, useState } from 'react';
import { coworkerApi } from '@/lib/api/coworker';
import { useAuthStore, useCoworkerStore } from '@/store';
import useMemoryStore from '@/store/memory';

const CACHE_TTL_MS = 5 * 60 * 1000;
const MEMORY_REFRESH_DEBOUNCE_MS = 1000;
const cache = new Map();
const pending = new Map();

function cacheKey(userId, collectionId) {
  return `v8:${userId || ''}:${collectionId || ''}`;
}

function readCache(key) {
  const hit = cache.get(key);
  if (!hit) return undefined;
  if (hit.revision !== useMemoryStore.getState().workStarterPromptsRevision || Date.now() - hit.at > CACHE_TTL_MS) {
    cache.delete(key);
    return undefined;
  }
  return hit.items;
}

function initialState(enabled, key) {
  if (!enabled) return { items: null, isPending: false, key };
  const cached = readCache(key);
  if (cached !== undefined) return { items: cached, isPending: false, key };
  return { items: null, isPending: true, key };
}

/**
 * Load memory-personalized Work composer pills written by Arya.
 * Returns `{ items, isPending }`. Hold the default capability pills until
 * `isPending` is false so they never flash in over a memory-generated set.
 */
export default function useWorkStarterPrompts({
  collectionId,
  enabled = true,
} = {}) {
  const token = useAuthStore((s) => s.token);
  const userId = useAuthStore((s) => s.user?.id);
  const memoryEnabled = useAuthStore((s) => s.user?.preferences?.memory?.isEnabled !== false);
  const includeMemory = useCoworkerStore((s) => s.composerIncludeMemory);
  const revision = useMemoryStore((s) => s.workStarterPromptsRevision);
  const key = cacheKey(userId, collectionId);
  const [state, setState] = useState(() => initialState(enabled, key));

  const canPersonalize = Boolean(enabled && token && userId && memoryEnabled && includeMemory);

  useEffect(() => {
    if (!canPersonalize) {
      setState({ items: null, isPending: false, key });
      return undefined;
    }

    const cached = readCache(key);
    if (cached !== undefined) {
      setState({ items: cached, isPending: false, key });
      return undefined;
    }

    let cancelled = false;
    setState((prev) => {
      const keep = prev.key === key && Array.isArray(prev.items);
      return {
        items: keep ? prev.items : null,
        isPending: !keep,
        key,
      };
    });
    const isCurrent = () => !cancelled
      && useMemoryStore.getState().workStarterPromptsRevision === revision;
    const load = () => {
      if (!isCurrent()) return;
      // Multiple composers can mount together; share one request per memory revision.
      const requestKey = `${key}:${revision}`;
      let request = pending.get(requestKey);
      if (!request) {
        request = coworkerApi.fetchStarterPrompts({ collectionId }).then((data) => {
          const next = data?.personalized && Array.isArray(data.suggestions) && data.suggestions.length
            ? data.suggestions
            : null;
          // A response started before a dream update must not restore stale cache entries.
          if (useMemoryStore.getState().workStarterPromptsRevision === revision) {
            cache.set(key, { at: Date.now(), items: next, revision });
          }
          return next;
        }).finally(() => pending.delete(requestKey));
        pending.set(requestKey, request);
      }
      request.then((next) => {
        if (isCurrent()) setState({ items: next, isPending: false, key });
      }).catch(() => {
        if (isCurrent()) setState({ items: null, isPending: false, key });
      });
    };

    // Dream commits arrive as a burst of memory and profile events after persistence.
    const changedAt = useMemoryStore.getState().workStarterPromptsChangedAt;
    const delay = Math.max(0, changedAt + MEMORY_REFRESH_DEBOUNCE_MS - Date.now());
    const timer = delay > 0 ? setTimeout(load, delay) : null;
    if (!delay) load();

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [canPersonalize, collectionId, key, revision]);

  if (!canPersonalize) return { items: null, isPending: false };
  return { items: state.items, isPending: state.isPending };
}
